import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, FileText, Layers, Loader2, XCircle } from "lucide-react";
import { API, api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return setFailed(true);
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setData(data);
        if (["failed", "expired"].includes(data.payment_status) || attempts.current > 15) return setFailed(true);
      } catch {
        if (attempts.current > 15) return setFailed(true);
      }
      attempts.current += 1;
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-16" data-testid="payment-success-page">
      <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#141419] p-8 text-center sm:p-10">
        {!data && !failed && (
          <>
            <Loader2 className="mx-auto animate-spin text-emerald-300" size={40} />
            <h1 className="font-heading mt-6 text-2xl font-bold text-white">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-zinc-500">Hold tight, this takes a few seconds.</p>
          </>
        )}

        {failed && (
          <>
            <XCircle className="mx-auto text-red-400" size={40} />
            <h1 className="font-heading mt-6 text-2xl font-bold text-white" data-testid="payment-failed-heading">Payment not confirmed</h1>
            <p className="mt-2 text-sm text-zinc-500">If you were charged, contact us with your receipt and we'll sort it out.</p>
            <Link to="/beats" className="mt-6 inline-block rounded-full border border-white/15 px-6 py-3 text-sm text-white" data-testid="payment-failed-back-link">Back to store</Link>
          </>
        )}

        {data && data.type === "beat" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-400" size={44} />
            <p className="mt-4 font-mono text-xs uppercase tracking-widest text-emerald-300">{data.tier_name}</p>
            <h1 className="font-heading mt-2 text-3xl font-extrabold text-white" data-testid="download-beat-title">"{data.beat_title}"</h1>
            <p className="mt-2 text-sm text-zinc-400">Your files are ready. A download link was also emailed to you.</p>
            <div className="mt-8 space-y-3">
              <a
                href={`${API}/download/${data.download_token}`}
                data-testid="download-audio-button"
                className="flex items-center justify-center gap-3 rounded-full bg-emerald-300 py-4 text-sm font-bold uppercase tracking-wider text-black transition-colors hover:bg-emerald-200"
              >
                <Download size={16} /> Download Beat (Untagged)
              </a>
              <a
                href={`${API}/download/${data.download_token}/license`}
                data-testid="download-license-button"
                className="flex items-center justify-center gap-3 rounded-full border border-emerald-300/40 py-4 text-sm font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-300/10"
              >
                <FileText size={16} /> Download PDF License
              </a>
              {data.tier === "wav" && data.has_stems && (
                <a
                  href={`${API}/download/${data.download_token}/stems`}
                  data-testid="download-stems-button"
                  className="flex items-center justify-center gap-3 rounded-full border border-emerald-300/40 py-4 text-sm font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-300/10"
                >
                  <Layers size={16} /> Download Stems (ZIP)
                </a>
              )}
            </div>
            <p className="mt-6 text-xs text-zinc-600">Keep this page bookmarked — your download links stay active.</p>
          </>
        )}

        {data && data.type === "deposit" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-400" size={44} />
            <h1 className="font-heading mt-6 text-3xl font-extrabold text-white" data-testid="booking-confirmed-heading">Session Locked In</h1>
            {data.booking && (
              <p className="mt-3 text-sm text-zinc-400" data-testid="booking-confirmed-details">
                {data.booking.service_type}<br />{data.booking.date} at {data.booking.time_slot}<br />
                <span className="text-zinc-500">ATL Metro Sound Lab — Studio A, Atlanta</span>
              </p>
            )}
            <p className="mt-4 text-xs text-zinc-500">A confirmation email is on its way. Balance due at the session.</p>
            <Link to="/" className="mt-6 inline-block rounded-full bg-emerald-300 px-8 py-3 text-sm font-bold uppercase tracking-wider text-black" data-testid="booking-confirmed-home-link">Back Home</Link>
          </>
        )}
      </div>
    </div>
  );
}
